import { Box, HStack, Icon, RippleButton, Text, type IconName } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import styles from "./QuestionHeader.module.scss";
import { useIsMobile } from "@repo/utils";

interface IconToggleButtonProps {
  icon: IconName;
  isActive?: boolean;
  onPress?: () => void;
}

const IconToggleButton = ({
  icon,
  isActive = false,
  onPress,
}: IconToggleButtonProps) => {
  const { theme } = useTheme();

  return (
    <RippleButton
      variant={isActive ? "primary" : "text"}
      shape="round"
      size="xs"
      iconOnly
      onPress={onPress}
      className={styles.iconButton}
    >
      <Icon
        name={icon}
        size={24}
        color={isActive ? theme.colors.neutral[100] : theme.colors.primary[40]}
      />
    </RippleButton>
  );
};

export interface QuestionHeaderProps {
  questionNumber: number;
  onClearSelection?: () => void;
  hasSelection?: boolean;
  isPanelOpen?: boolean;
  onTogglePanel?: () => void;
  isListOpen?: boolean;
  onToggleList?: () => void;
  onFlagToggle?: () => void;
  hideControls?: boolean;
  showTextView?: boolean;
  showGridView?: boolean;
  isClearDisabled?: boolean;
  hideClearSelection?: boolean;
}

export const QuestionHeader = ({
  questionNumber,
  onClearSelection,
  hasSelection = false,
  isPanelOpen = false,
  onTogglePanel,
  isListOpen = false,
  onToggleList,
  onFlagToggle,
  hideControls = false,
  showTextView = true,
  showGridView = true,
  isClearDisabled = false,
  hideClearSelection = false,
}: QuestionHeaderProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");
  const isMobile = useIsMobile();
  const isClearHiddenOnMobile = isMobile && !hasSelection;
  return (
    <HStack className={styles.header} align="center" justify="space-between">
      <Text variant="body" size="large" className={styles.questionNumber}>
        Q {questionNumber}
      </Text>

      {!hideControls && (
        <HStack gap="md" align="center">
          {!hideClearSelection && (
            <RippleButton
              variant="text"
              size="md"
              onPress={onClearSelection}
              disabled={
                !isClearHiddenOnMobile && (!hasSelection || isClearDisabled)
              }
              style={
                isClearHiddenOnMobile
                  ? { opacity: 0, pointerEvents: "none" }
                  : undefined
              }
            >
              {t("exam_clear_selection")}
            </RippleButton>
          )}

          <HStack gap="sm" align="center">
            {showGridView && (
              <Box className={styles.mobileHideGrid}>
                <IconToggleButton
                  icon="gridView"
                  isActive={isPanelOpen}
                  onPress={onTogglePanel}
                />
              </Box>
            )}
            {showTextView && (
              <IconToggleButton
                icon="textView"
                isActive={isListOpen}
                onPress={onToggleList}
              />
            )}
            <IconToggleButton icon="flag" onPress={onFlagToggle} />
            {!isPanelOpen && !isListOpen && (
              <Box className={styles.mobileHideGrid}>
                <RippleButton
                  variant="text"
                  shape="round"
                  size="sm"
                  onPress={onTogglePanel}
                  className={styles.innerExpandButton}
                >
                  <Icon
                    name="doubleArrowRight"
                    size={14}
                    color={theme.colors.primary[40]}
                  />
                </RippleButton>
              </Box>
            )}
          </HStack>
        </HStack>
      )}
    </HStack>
  );
};
